// @noflow
import { get, flowRight, find } from 'lodash/fp';
import * as actions from './actions';
import { alerts, application } from '~/core/constants';
import type {
    AttemptLoginAction,
    HideComponentAction,
    SetupCookiesAction,
    SetupDashboardConfigurationAction,
    SetupEntitlementsAction,
    SetupMomentAction,
    SetupUserActions,
    SetupUserDevicesAction
} from './types';
import { auth as authApi, nodes as nodesApi } from '~/core/api';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { dismissAllAlerts, requestAlert } from '~/core/state/alerts/actions';
import { getDashboardCustomisation, receiveDashboardCustomisation } from '~/core/state/dashboard/actions';
import { getPostcode, getUserCountry } from '~/core/state/user/selectors';
import { logIn, logInComplete, receiveGTMOffers, showWelcomeMessage } from './actions';

import { buildKey } from '~/core/utils/app-settings';
import Cookie from '<drivers>/cookie';
import { dateTimeInterval } from '~/core/state/hub/actions';
import { deviceTypes, capabilities } from '~/core/constants/devices';
import { getAppSetting } from '~/core/state/app-settings/selectors';
import { getAppSettings, setAppSettings } from '~/core/state/app-settings/actions';
import { getConfig } from '~/core/state/config/selectors';
import { getEntitlements } from '~/core/state/entitlements/actions';
import { getHolidayModeDataSaga } from '~/core/state/holiday-mode/sagas';
import { getHub, getHubHasCapability } from '~/core/state/devices/selectors';
import { getWeather } from '~/core/state/weather/actions';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import { injectGTMLoader, isGTMActive } from './gtm';
import type { Effect } from 'redux-saga';
import { isMicrosoftEdge } from '~/core/utils/browser';
import { LOAD_COUNTRY_CONFIG } from '~/core/state/config/constants';
import { RECEIVE_APP_SETTINGS } from '~/core/state/app-settings/constants';
import { loadCookies } from '~/core/state/cookies/actions';
import messages from './intl';
import moment from 'moment-timezone';
import { fromJS } from 'immutable';
import { getTemplates, receiveActions } from '~/core/state/actions/actions';
import { receiveHomes } from '~/core/state/homes/actions';
import { receiveDevices } from '~/core/state/devices/actions';
import { receiveProducts } from '~/core/state/products/actions';
import { MODAL_DISMISSED } from '~/core/constants/app-settings';
import { filterOutDeviceGroups } from '~/core/state/products/utils';

const errorCodeMap = (code: string): { [boolean]: $npm$ReactIntl$MessageDescriptor } => ({
    [true]: messages.loginUnexpectedError,
    [code === application.loginStatuses.ACCOUNT_LOCKED]: messages.loginAccountLocked,
    [code === application.loginStatuses.NOT_AUTHORIZED]: messages.loginNotAuthorized
});

const isHiveLinkPrimary = flowRight(t => t === 'assistedLivingHomes', get('type'), find(h => h.primary), get('homes'));

export function* attemptLogIn({
    payload: { username, password, preventHiveLinkLogin = true, nextPathname }
}: AttemptLoginAction): Generator<Effect, *, *> {
    const [responseJson, error] = yield call(handleApi(authApi.login), username, password, true);

    // Hive Link hack - don't like customer's who's primary home is HL login at all
    if (!error && preventHiveLinkLogin && responseJson.homes && isHiveLinkPrimary(responseJson.homes)) {
        yield put(logInComplete(new Error('Login Failed')));
        return yield put(requestAlert(messages.hiveLinkUnsupported, alerts.types.ERROR, true));
    }

    if (!error) {
        const deviceGroupsEnabled = yield select(getConfig, 'features:deviceGroups');
        return yield put(logIn({ ...responseJson, deviceGroupsEnabled, nextPathname, manualLogin: true }));
    }

    yield put(logInComplete(new Error('Login Failed')));
    let alert;

    if (isMicrosoftEdge() && error instanceof DOMException) {
        // M$ Edge Don't handle a 401 response on a CORS resource
        // the same way as other browsers - so this detects
        // a weird response and treats it like a login failure
        alert = messages.loginNotAuthorized;
    } else {
        if (error.code !== 401) {
            return yield put(requestAlert(messages.loginUnexpectedError, alerts.types.ERROR, true));
        }
        alert = errorCodeMap(error.status)[true];
    }

    yield put(requestAlert(alert, alerts.types.ERROR));
}

function* watchAttemptLogIn(): Generator<Effect, *, *> {
    yield takeEvery(actions.attemptLogIn, handleSaga(attemptLogIn));
}

export function* setupCookies({
    payload: { token, platform, nextPathname }
}: SetupCookiesAction): Generator<Effect, *, *> {
    const countryCode = yield select(getUserCountry);
    yield call(Cookie.set, 'user-region', countryCode);
    yield call(Cookie.set, 'token', token);

    if (platform) {
        yield call(Cookie.set, 'endpoint', platform.endpoint);
        yield call(Cookie.set, 'honeycomb', platform.honeycomb);
    }
}

export function* setupHub(): Generator<Effect, *, *> {
    yield put(dateTimeInterval(moment().valueOf()));
}

export function* setupMoment({ payload }: SetupMomentAction): Generator<Effect, *, *> {
    yield call(moment.locale, payload.user.locale);
}

export function* setupUserActions({ payload: { actions } }: SetupUserActions): Generator<Effect, *, *> {
    if (actions && actions.length) {
        yield put(receiveActions(actions));
        yield put(getTemplates());
    }
}

export function* setupUserHomes({ payload: { homes } }: SetupUserActions): Generator<Effect, *, *> {
    if (homes) {
        yield put(receiveHomes(homes));
    }
}
export function* setupUserDevices({
    payload: { devices, products, deviceGroupsEnabled }
}: SetupUserDevicesAction): Generator<Effect, *, *> {
    if (devices.length) {
        yield put(receiveDevices(devices));
    }

    if (products.length) {
        const hubSupportsGroups = yield select(getHubHasCapability, capabilities.PRODUCT_GROUP);
        const filteredProducts = deviceGroupsEnabled && hubSupportsGroups ? products : filterOutDeviceGroups(products);

        yield put(receiveProducts({ products: filteredProducts, loadedAt: new Date() }));
    }
}

export function* setupEntitlements({ payload }: SetupEntitlementsAction): Generator<Effect, *, *> {
    yield put(getEntitlements({ userid: payload.user.id }));
}

export function* setupAppSettings(): Generator<Effect, *, *> {
    yield put(getAppSettings());
}

export function* setupDashboardConfiguration({
    payload: { actions, products }
}: SetupDashboardConfigurationAction): Generator<Effect, *, *> {
    const isDashboardCustomisationDataOn = yield select(getConfig, 'features:dashboardCustomisationData');
    if (isDashboardCustomisationDataOn) {
        yield put(getDashboardCustomisation());
    } else {
        yield put(receiveDashboardCustomisation({ customisation: { pages: [] }, products, actions }));
    }
}

export function* confirmLoginCompleteSaga(): Generator<Effect, *, *> {
    yield put(logInComplete());
}

export function* getWeatherSaga(): Generator<Effect, *, *> {
    const postcode = yield select(getPostcode);
    const countryCode = yield select(getUserCountry);

    if (postcode && countryCode) {
        yield put(
            getWeather({
                postcode,
                countryCode
            })
        );
    }
}

function* setUpApp(action: *): Generator<Effect | Array<*>, *, *> {
    yield put(loadCookies());
    yield all([
        setupCookies(action),
        setupUserDevices(action),
        setupUserActions(action),
        setupUserHomes(action),
        setupAppSettings(),
        setupEntitlements(action),
        setupHub(),
        setupMoment(action),
        getWeatherSaga()
    ]);

    yield* setupDashboardConfiguration(action);
    yield* confirmLoginCompleteSaga(action);
    yield* getHolidayModeDataSaga();
}

function* watchLogIn(): Generator<Effect, *, *> {
    yield takeLatest(actions.logIn, handleSaga(setUpApp));
}

export function* logOut(): Generator<Effect, *, *> {
    yield put(dismissAllAlerts());

    const hub = yield select(getHub);

    if (hub && hub.state.discovery) {
        yield call(handleApi(nodesApi.patchNode), { discovery: false }, deviceTypes.HUB, hub.id);
    }

    yield call(Cookie.remove, 'token');
    yield call(Cookie.remove, 'endpoint');
    yield call(Cookie.remove, 'honeycomb');

    // this is the "old" pre-sso behaviout - re-enable this
    // if you want to wipe local state completely. If you do this
    // then the store changes too quickly for react-router to bounce
    // the user to to the SSO logout path.
    // yield put(actions.ResetState());
}

function* watchLogOut(): Generator<Effect, *, *> {
    yield takeEvery(actions.logOut, handleSaga(logOut));
}

function* setupGTM(): Generator<Effect, *, *> {
    const gtmContainerId = yield select(getConfig, 'gtmContainerId');
    if (gtmContainerId) {
        yield call(injectGTMLoader, gtmContainerId);
    }
}

function* setupHiveOffers(): Generator<Effect, *, *> {
    const showShopMenu = yield select(getConfig, 'features:shopMenuLink');
    const countryCode = yield select(getUserCountry);
    if (showShopMenu) {
        while (!global.hivehome_gtm_offers) {
            yield call(delay, 1000);
        }
        yield put(receiveGTMOffers(global.hivehome_gtm_offers[countryCode]));
    }
}

function* setupGtmTag(tag): Generator<Effect, *, *> {
    while (!global[tag]) {
        yield call(delay, 1000);
    }

    const countryCode = yield select(getUserCountry);
    const gtmTag = global[tag][countryCode];
    const dissmissedModal = yield gtmTag && select(getAppSetting, buildKey(MODAL_DISMISSED, gtmTag.id));

    if (dissmissedModal || !isGTMActive(fromJS(gtmTag))) {
        return;
    }

    const fnMap = {
        hivehomeWelcomeMessage: showWelcomeMessage
    };

    const fn = fnMap[tag];

    if (fn) {
        yield put(fn(gtmTag));
    }
}

function* watchGtmConfig(): Generator<Array<*>, *, *> {
    if (process.env.PLATFORM === 'web') {
        yield all([
            takeEvery(LOAD_COUNTRY_CONFIG, handleSaga(setupGTM)),
            takeEvery(LOAD_COUNTRY_CONFIG, handleSaga(setupHiveOffers)),
            takeEvery(RECEIVE_APP_SETTINGS, handleSaga(() => setupGtmTag('hivehomeWelcomeMessage')))
        ]);
    }
}

export function* hideModal({ payload: { id } }: HideComponentAction): Generator<Effect, *, *> {
    yield put(setAppSettings({ [buildKey(MODAL_DISMISSED, id)]: true }));
}

function* watchHideModal(): Generator<Effect, *, *> {
    yield takeEvery(actions.hideWelcomeMessage, handleSaga(hideModal));
}

export default [watchAttemptLogIn, watchLogIn, watchLogOut, watchGtmConfig, watchHideModal];



// WEBPACK FOOTER //
// ./src/core/state/application/sagas.js